import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect, useCallback } from "react";
import { useAsyncTaskPolling } from "../hooks/useAsyncTaskPolling";
import { useDraft } from "../hooks/useDraft";
import usePersistedToggle from "../hooks/usePersistedToggle";
import { useUser } from "../contexts/UserContext";
import StreamingMicButton from "./StreamingMicButton";
import PrivacySelector from "./PrivacySelector";
import RegenerateTtsDialog from "./RegenerateTtsDialog";
import ApplyToRepliesDialog from "./ApplyToRepliesDialog";
import SplitContentDialog, { NODE_CHAR_CAP } from "./SplitContentDialog";
import PublicReplyDialog, { PUBLIC_REPLY_ACK_KEY } from "./PublicReplyDialog";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { useToast } from "../contexts/ToastContext";
import api from "../api";
import { uploadFileInChunks } from "../utils/chunkedUpload";
import {
  isSpendBlocked, notifySpendBlocked, isSpendCapError, spendCapToastMessage,
} from "../utils/spendCap";
import useSubmitShortcut from "../hooks/useSubmitShortcut";

const NodeForm = forwardRef(
  (
    { parentId, onSuccess, hideSubmit, initialContent, editMode = false, nodeId, initialPrivacyLevel, initialAiUsage, detachPrompt, hidePowerFeatures = false, placeholder, onSubmitOverride, uploadReplyOptions, compact = false, hideAudioUpload = false, allowAgenticPrompt = false, hasGeneratedTts = false, aiUsageFromGlobalDefault = false, hasChildren = false },
    ref
  ) => {
    const { user } = useUser();
    const isOnline = useOnlineStatus();
    const { addToast } = useToast();
    const [content, setContent] = useState(initialContent || "");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [uploadedNodeId, setUploadedNodeId] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [hasDraft, setHasDraft] = useState(false);
    // When editing an entry that has generated TTS, ask whether to keep or
    // regenerate the now-stale audio before saving (#66).
    const [showTtsDialog, setShowTtsDialog] = useState(false);
    // Editing a node that has replies and changing its privacy / AI usage:
    // ask whether the change is for this node only or also for the user's
    // replies below it, before saving. pendingResumeRef carries the TTS
    // choice already made so the resumed save keeps it.
    const [showScopeDialog, setShowScopeDialog] = useState(false);
    const pendingResumeRef = useRef({});

    // Per-entry character cap: oversized new entries are split
    // server-side into serially connected entries after explicit
    // consent (paste or submit opens the dialog). pendingPaste holds
    // paste text awaiting that consent.
    const [showSplitDialog, setShowSplitDialog] = useState(false);
    const [showPublicReplyDialog, setShowPublicReplyDialog] = useState(false);
    // Parent's privacy, tracked whenever replying — drives the public-reply
    // consent dialog (#228). Independent of the inherit-defaults effect.
    const [parentPrivacy, setParentPrivacy] = useState(null);
    const [pendingPaste, setPendingPaste] = useState(null);
    const [splitAcknowledged, setSplitAcknowledged] = useState(false);

    // Remember the user's last privacy / AI-usage choices for fresh
    // top-level entries (#63), so they don't have to re-pick every time.
    // Only consulted when there's no more-specific source: edit-mode
    // (initialPrivacyLevel/initialAiUsage), parent inheritance (parentId),
    // and explicit props all take precedence over these remembered
    // values. Mirrors the localStorage pattern used by loore_auto_generate.
    const remembersChoices = !editMode && !parentId
      && !initialPrivacyLevel && !initialAiUsage;
    const readRemembered = (key, fallback) => {
      if (!remembersChoices) return fallback;
      const stored = localStorage.getItem(key);
      return stored === null ? fallback : stored;
    };

    // Privacy settings state - use initial values if provided (edit mode),
    // then remembered choice (fresh top-level entry), then user defaults
    // from context, then hardcoded fallback.
    // For new nodes with a parent, we'll update these after fetching the parent.
    const [privacyLevel, setPrivacyLevel] = useState(
      initialPrivacyLevel
      || readRemembered('loore_last_privacy_level', user?.default_privacy_level)
      || "private"
    );
    // Fresh agentic sessions (Voice/Text) take ai_usage from the user's
    // global default rather than the last-used value — the global setting
    // is the user's standing AI-interaction consent. Plain journaling
    // entries still remember the last choice.
    const [aiUsage, setAiUsage] = useState(
      initialAiUsage
      || (aiUsageFromGlobalDefault
            ? user?.default_ai_usage
            : readRemembered('loore_last_ai_usage', user?.default_ai_usage))
      || "none"
    );

    // Persist choices whenever the user changes them on a fresh top-level
    // entry, so the next Write dialog opens with the same selections.
    useEffect(() => {
      if (remembersChoices) {
        localStorage.setItem('loore_last_privacy_level', privacyLevel);
      }
    }, [privacyLevel, remembersChoices]);
    useEffect(() => {
      if (remembersChoices && !aiUsageFromGlobalDefault) {
        localStorage.setItem('loore_last_ai_usage', aiUsage);
      }
    }, [aiUsage, remembersChoices, aiUsageFromGlobalDefault]);
    // "Agentic Reply" — attaches the textmode system prompt (profile +
    // recent + todo + prefs as context). ON by default wherever it is
    // offered (#261: a first message without it is the weakest possible
    // first impression, and a pre-loaded account only pays off on the
    // agentic path) and remembered in localStorage like Auto-generate, so
    // switching it off is respected while a fresh account starts agentic.
    const [useAgenticPrompt, setUseAgenticPrompt] = usePersistedToggle(
      'loore_agentic_reply', allowAgenticPrompt);

    // "Auto-generate" — does an LLM reply fire automatically on submit,
    // so the user doesn't have to click LLM Response manually on the
    // resulting node? Tied to the same `loore_auto_generate`
    // localStorage key that NodeDetail reads, so there's ONE preference
    // for "auto-generate after submit" across the app. Default true on
    // a fresh install. Orthogonal to Agentic Reply: if Agentic is ON
    // the LLM fires via /textmode/start regardless; if Agentic is OFF
    // and Auto-generate is ON we follow the /nodes/ POST with an
    // explicit /nodes/<id>/llm call.
    const [useAutoGenerate, setUseAutoGenerate] = usePersistedToggle(
      'loore_auto_generate', allowAgenticPrompt);

    // Draft auto-save hook
    const {
      draft,
      isLoaded: isDraftLoaded,
      saveDraft,
      deleteDraft,
      lastSaved,
      isSaving: isDraftSaving
    } = useDraft({
      nodeId: editMode ? nodeId : null,
      parentId: editMode ? null : parentId
    });

    // Audio file upload state
    const [uploadedFile, setUploadedFile] = useState(null);
    const fileInputRef = React.useRef(null);

    // Track streaming session ID when transcription completes (for "review before save" flow)
    // With draft-based streaming, no node exists until user explicitly saves
    const [streamingSessionId, setStreamingSessionId] = useState(null);
    // Recovery: transcribing stored chunks from a recovered draft
    const [isRecoveringAudio, setIsRecoveringAudio] = useState(false);
    // Track content that existed before streaming started (to append new transcript to it)
    const preStreamingContentRef = React.useRef("");
    const textareaRef = React.useRef(null);

    // Track if streaming is in progress (set by StreamingMicButton callbacks)
    const [isStreamingRecording, setIsStreamingRecording] = useState(false);

    // Transcription polling (for non-streaming mode)
    const {
      status: transcriptionStatus,
      progress: transcriptionProgress,
      data: transcriptionData,
      error: transcriptionError,
      startPolling: startTranscriptionPolling
    } = useAsyncTaskPolling(
      uploadedNodeId ? `/nodes/${uploadedNodeId}/transcription-status` : null,
      { enabled: false }
    );

    // Fetch parent node privacy settings when creating a child node
    useEffect(() => {
      // Only fetch parent if we have a parentId and we're not in edit mode
      // and we haven't explicitly provided initial privacy settings
      if (parentId && !editMode) {
        api.get(`/nodes/${parentId}`)
          .then((response) => {
            const parent = response.data;
            setParentPrivacy(parent.privacy_level || "private");
            if (!initialPrivacyLevel && !initialAiUsage) {
              // Set privacy settings to match parent's settings. AI usage
              // is the backend's reply default (#362): the parent's, but
              // under a Read the thread's, since a Read's nodes are 'chat'
              // only for the tweets they quote.
              setPrivacyLevel(parent.privacy_level || "private");
              setAiUsage(parent.reply_ai_usage || parent.ai_usage || "none");
            }
          })
          .catch((err) => {
            console.error("Error fetching parent node:", err);
            // If we can't fetch parent, keep the defaults
          });
      }
    }, [parentId, editMode, initialPrivacyLevel, initialAiUsage]);

    // Replies in a public thread are public (#228): pin the STATE, not just
    // the display — a remembered 'private' from loore_last_privacy_level
    // must never reach the submit path.
    useEffect(() => {
      if (!editMode && parentPrivacy === "public"
          && privacyLevel !== "public") {
        setPrivacyLevel("public");
      }
    }, [parentPrivacy, privacyLevel, editMode]);

    // Auto-populate form with draft when loaded
    useEffect(() => {
      if (isDraftLoaded && draft && draft.content) {
        // Auto-populate the form with draft content
        setContent(draft.content);
        setHasDraft(true);
      }
    }, [isDraftLoaded, draft]);

    // Recovery: when a draft has stored (untranscribed) audio chunks,
    // trigger server-side transcription and poll for completion.
    //
    // This still fires on its own, but only for a session nobody is
    // recording: GET /drafts/ no longer returns a live session (#320), so
    // what arrives here was left behind (reload, crash, navigating away
    // mid-recording) and the transcript should come back into this box
    // without a click. If the session turns out to be live after all (its
    // tab resumed it), the server refuses (409) or reports `live`, and
    // this view lets go of it instead of completing it.
    useEffect(() => {
      if (!isDraftLoaded || !draft) return;
      if (!draft.session_id || !draft.has_stored_chunks) return;

      setIsRecoveringAudio(true);
      setStreamingSessionId(draft.session_id);

      const letGo = () => {
        setIsRecoveringAudio(false);
        setStreamingSessionId(null);
      };

      const triggerRecovery = async () => {
        try {
          try {
            await api.post(`/drafts/streaming/${draft.session_id}/transcribe-remaining`);
          } catch (err) {
            if (err.response?.status === 409) {
              console.warn('[NodeForm] Recording is live in another tab; not recovering it here');
              letGo();
              return;
            }
            throw err;
          }

          // Poll for completion
          const pollInterval = setInterval(async () => {
            try {
              const res = await api.get(`/drafts/streaming/${draft.session_id}/status`);
              const { streaming_status, content, live } = res.data;

              if (live) {
                clearInterval(pollInterval);
                letGo();
                return;
              }

              if (content) {
                setContent(content);
              }

              if (streaming_status === 'completed' || streaming_status === 'failed') {
                clearInterval(pollInterval);
                setIsRecoveringAudio(false);
                if (content) {
                  saveDraft(content);
                  setHasDraft(true);
                }
              }
            } catch (err) {
              console.error('[NodeForm] Recovery poll error:', err);
              clearInterval(pollInterval);
              setIsRecoveringAudio(false);
            }
          }, 3000);

          // Safety timeout: stop polling after 5 minutes
          setTimeout(() => {
            clearInterval(pollInterval);
            setIsRecoveringAudio(false);
          }, 5 * 60 * 1000);
        } catch (err) {
          console.error('[NodeForm] Failed to trigger audio recovery:', err);
          setIsRecoveringAudio(false);
        }
      };

      triggerRecovery();
    }, [isDraftLoaded, draft]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle discard draft
    const handleDiscardDraft = useCallback(() => {
      deleteDraft();
      setHasDraft(false);
      // Reset content to initial value (empty for new nodes, original content for edit mode)
      setContent(initialContent || "");
    }, [deleteDraft, initialContent]);

    // A new entry is sent: the form is cleared before the caller moves on.
    // The page can stay on screen until the entry's node loads
    // (NodeDetailWrapper, useNodePrefetch), and the sent text must not be
    // sendable twice. An edit keeps its text.
    const succeed = useCallback((data) => {
      if (!editMode) {
        setContent("");
        setUploadedFile(null);
      }
      onSuccess(data);
    }, [editMode, onSuccess]);
    // For the transcription effect below, which must not re-run when only
    // the caller's `onSuccess` changes: a page that re-renders while the
    // entry's node loads (WritePage's spinner) would otherwise handle the
    // same completed upload twice (its toasts shown twice).
    const succeedRef = useRef(succeed);
    useEffect(() => { succeedRef.current = succeed; });

    // Auto-start polling when uploadedNodeId is set
    useEffect(() => {
      if (uploadedNodeId) {
        startTranscriptionPolling();
      }
    }, [uploadedNodeId, startTranscriptionPolling]);

    // Handle transcription completion
    useEffect(() => {
      if (transcriptionStatus === 'completed' && transcriptionData) {
        setLoading(false);
        // Delete draft after successful transcription
        deleteDraft();
        setHasDraft(false);
        // A Text-mode upload's reply (#342): land on the entry with
        // ?awaitLlm like a typed or recorded one, or say why it was skipped.
        (transcriptionData.warnings || []).forEach((w) => addToast(w, 10000));
        const normalizedData = {
          ...transcriptionData,
          id: transcriptionData.node_id,
          user_node_id: transcriptionData.node_id,
          ...(transcriptionData.llm_node_id
            && { awaitLlm: transcriptionData.llm_node_id }),
        };
        succeedRef.current(normalizedData);
        setUploadedNodeId(null);
      } else if (transcriptionStatus === 'failed') {
        setLoading(false);
        setError(transcriptionError || 'Transcription failed');
        setUploadedNodeId(null);
      }
    }, [transcriptionStatus, transcriptionData, transcriptionError, deleteDraft, addToast]);

    const handleFileSelect = (event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file size (200 MB)
      const maxSize = 200 * 1024 * 1024;
      if (file.size > maxSize) {
        setError("File size must be under 200 MB");
        return;
      }

      // Validate file type
      const allowedTypes = ['audio/webm', 'audio/wav', 'audio/wave', 'audio/x-wav', 'audio/m4a', 'audio/x-m4a',
                           'audio/mp3', 'audio/mpeg', 'audio/mp4', 'audio/ogg', 'audio/flac', 'audio/aac', 'audio/x-aac'];
      const allowedExts = ['.webm', '.wav', '.m4a', '.mp3', '.mp4', '.mpeg', '.mpga', '.ogg', '.oga', '.flac', '.aac'];

      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
        setError("Invalid file type. Please upload an audio file (mp3, wav, m4a, webm, ogg, flac, aac)");
        return;
      }

      setError("");
      setUploadedFile(file);
      // Clear text content
      setContent("");
    };

    const handleSubmit = async (event, regenerateTts, splitConfirmed, publicConfirmed, applyToReplies) => {
      event && event.preventDefault();
      // Validate: require content or audio
      if (!editMode && uploadedFile) {
        // Submit uploaded audio file
      } else if (!content.trim()) {
        setError("Content is required.");
        return;
      }

      // Editing an entry that has generated TTS and whose text actually
      // changed: ask whether to keep or regenerate the now-stale audio
      // before saving (#66). `regenerateTts` is undefined on the initial
      // submit (→ open the dialog) and a boolean once the user has chosen.
      if (
        editMode && nodeId && hasGeneratedTts && regenerateTts === undefined
        && content !== initialContent
      ) {
        setShowTtsDialog(true);
        return;
      }

      // Changing privacy / AI usage on a node that has replies: ask whether
      // the change is for this node only or also for the user's replies.
      // `applyToReplies` is undefined on the initial submit (→ open the
      // dialog) and a boolean once the user has chosen.
      if (
        editMode && nodeId && hasChildren && applyToReplies === undefined
        && (
          (initialPrivacyLevel != null && privacyLevel !== initialPrivacyLevel)
          || (initialAiUsage != null && aiUsage !== initialAiUsage)
        )
      ) {
        pendingResumeRef.current = { regenerateTts };
        setShowScopeDialog(true);
        return;
      }

      // Per-entry character cap. New entries can split into serially
      // connected entries (server-side, after consent via the dialog);
      // edits and custom-submit conversations can't, so they get a
      // plain error instead.
      if (content.length > NODE_CHAR_CAP && !uploadedFile) {
        // A recorded transcript (streamingSessionId) saves through
        // save-as-node, which splits server-side, even on a custom-submit
        // page — only typed custom submits and edits get the hard error.
        if (editMode || (onSubmitOverride && !streamingSessionId)) {
          setError(
            `This entry is ${content.length.toLocaleString()} characters — ` +
            `above the ${NODE_CHAR_CAP.toLocaleString()}-character limit. ` +
            `Please move part of it into separate entries.`
          );
          return;
        }
        if (!splitAcknowledged && !splitConfirmed) {
          setShowSplitDialog(true);
          return;
        }
      }

      // Replying under a PUBLIC node: the reply is public (#228, enforced
      // server-side too; the state is pinned public by the effect above).
      // Make the consent felt once — the dialog offers don't-show-again.
      if (
        !editMode && parentPrivacy === "public" && !publicConfirmed
        && localStorage.getItem(PUBLIC_REPLY_ACK_KEY) !== "true"
      ) {
        setShowPublicReplyDialog(true);
        return;
      }

      setLoading(true);
      setError("");
      try {
        let response;

        // Custom submit (e.g. /textmode/start). Skips /nodes/ POST entirely
        // and still handles draft cleanup + onSuccess for the caller. A
        // recorded entry (transcript held in a streaming draft, audio
        // attached) goes through the same override with its session id,
        // so the page applies one policy to typed and recorded entries —
        // previously it bypassed the override and landed as a bare node
        // with no system prompt and no auto-generated reply.
        if (onSubmitOverride && !editMode && !uploadedFile) {
          const data = await onSubmitOverride({
            content,
            parent_id: parentId,
            privacy_level: privacyLevel,
            ai_usage: aiUsage,
            streaming_session_id: streamingSessionId || undefined,
          });
          deleteDraft();
          setHasDraft(false);
          setStreamingSessionId(null);
          // Spend cap: backend kept the system + user nodes but skipped the
          // LLM placeholder (e.g. /textmode/start). Surface the banner.
          if (data && data.spend_capped) {
            try {
              window.dispatchEvent(new CustomEvent('loore:spend-capped', {}));
            } catch (e) { /* no-op */ }
          }
          // The entry was saved but the reply was refused (e.g. an
          // uncapped {user_export} on a non-Pro plan): say why.
          if (data && data.llm_error) addToast(data.llm_error, 10000);
          succeed(data);
          setLoading(false);
          return;
        }

        // Opt-in agentic top-level entry: route through /textmode/start
        // so the new thread gets a system node with the textmode prompt
        // attached. Whether an LLM placeholder is also created depends
        // on the independent Auto-generate toggle — when it's off, the
        // backend skips the LLM and returns only conversation/user IDs,
        // letting the user click LLM Response manually later after any
        // edits or follow-up nodes they want to add first.
        if (useAgenticPrompt && !editMode && !uploadedFile
            && !streamingSessionId && !parentId && aiUsage !== 'none'
            && content.length <= NODE_CHAR_CAP) {
          const res = await api.post('/textmode/start', {
            content,
            privacy_level: privacyLevel,
            ai_usage: aiUsage,
            auto_generate: useAutoGenerate,
          });
          deleteDraft();
          setHasDraft(false);
          // Spend cap: backend kept the system + user nodes but skipped the
          // LLM placeholder. Surface the banner; the no-llm_node_id branch
          // below lands the user on their new thread without a placeholder.
          if (res.data.spend_capped) {
            try {
              window.dispatchEvent(new CustomEvent('loore:spend-capped', {}));
            } catch (e) { /* no-op */ }
          }
          if (res.data.llm_error) addToast(res.data.llm_error, 10000);
          if (res.data.llm_node_id) {
            // Through the USER node so the entry gets its own URL/history
            // step; ?awaitLlm hands the pending LLM response to NodeDetail,
            // which goes on to it.
            succeed({
              id: res.data.user_node_id,
              awaitLlm: res.data.llm_node_id,
              ...res.data,
            });
          } else {
            succeed({
              id: res.data.user_node_id,
              ...res.data,
            });
          }
          setLoading(false);
          return;
        }

        // Handle streaming transcription completion - draft exists, create node from it
        if (streamingSessionId) {
          // Save the streaming draft as a node with any edits the user made.
          // Top-level entries carry the same decisions the typed paths make
          // below: Agentic Reply → textmode system node (like
          // /textmode/start), Auto-generate → LLM reply (like the
          // /nodes/<id>/llm follow-up). Replies (parentId) send neither —
          // NodeDetail fires auto-generate for those itself.
          const topLevelWithAi = !parentId && aiUsage !== 'none';
          const response = await api.post(`/drafts/streaming/${streamingSessionId}/save-as-node`, {
            content,
            ...(topLevelWithAi && useAgenticPrompt && { agentic: true }),
            ...(topLevelWithAi && useAutoGenerate && allowAgenticPrompt
              && { auto_generate: true }),
          });
          deleteDraft();
          setHasDraft(false);
          setStreamingSessionId(null);
          if (response.data.spend_capped) {
            try {
              window.dispatchEvent(new CustomEvent('loore:spend-capped', {}));
            } catch (e) { /* no-op */ }
          }
          // Land on the entry; ?awaitLlm hands a pending reply to NodeDetail.
          succeed({
            ...response.data,
            ...(response.data.llm_node_id && { awaitLlm: response.data.llm_node_id }),
          });
          setLoading(false);
          return;
        }

        if (editMode && nodeId) {
          // Update (edit) existing node (text only)
          response = await api.put(`/nodes/${nodeId}`, {
            content,
            privacy_level: privacyLevel,
            ai_usage: aiUsage,
            // Detaching the pinned prompt only makes sense when the text was
            // actually edited; a privacy/AI-usage-only save keeps it.
            ...(detachPrompt && content !== initialContent && { detach_prompt: true }),
            ...(regenerateTts && { regenerate_tts: true }),
            // Cascade the privacy / AI-usage change to the user's replies
            // (chosen in ApplyToRepliesDialog).
            ...(applyToReplies && { apply_to_descendants: true }),
          });
        } else if (uploadedFile) {
          // Upload audio file
          const fileToUpload = uploadedFile;

          // The same two decisions a typed or recorded entry makes (#342):
          // Agentic Reply → textmode system node, Auto-generate → an LLM
          // reply once the transcript exists (the transcription task
          // creates it). A page with its own submit policy (Text mode)
          // supplies them; otherwise the form's toggles do, for top-level
          // entries only, as in the recorded branch above.
          const topLevelWithAi = !parentId && aiUsage !== 'none';
          const replyOptions = uploadReplyOptions
            ? (uploadReplyOptions({ ai_usage: aiUsage }) || {})
            : {
              ...(topLevelWithAi && useAgenticPrompt && { agentic: true }),
              ...(topLevelWithAi && useAutoGenerate && allowAgenticPrompt
                && { auto_generate: true }),
            };

          // Check file size - use chunked upload for files larger than 10MB
          const useChunkedUpload = fileToUpload.size > 10 * 1024 * 1024;

          if (useChunkedUpload) {
            // Use chunked upload for large files
            setIsUploading(true);
            setUploadProgress(0);

            try {
              const uploadResult = await uploadFileInChunks(
                fileToUpload,
                {
                  parent_id: parentId,
                  node_type: 'user',
                  privacy_level: privacyLevel,
                  ai_usage: aiUsage,
                  ...replyOptions,
                },
                (progress) => {
                  setUploadProgress(progress);
                }
              );

              setIsUploading(false);
              setUploadProgress(100);

              // uploadFileInChunks returns data directly (not axios response)
              const nodeId = uploadResult.id;
              setUploadedNodeId(nodeId);
              // Keep loading state active while transcription is in progress
              // Polling will start automatically via useEffect
              return;
            } catch (uploadErr) {
              setIsUploading(false);
              setUploadProgress(0);
              throw uploadErr;
            }
          } else {
            // Use traditional upload for small files (< 10MB)
            const formData = new FormData();
            formData.append('audio_file', fileToUpload);
            if (parentId) formData.append('parent_id', parentId);
            formData.append('privacy_level', privacyLevel);
            formData.append('ai_usage', aiUsage);
            if (replyOptions.agentic) formData.append('agentic', 'true');
            if (replyOptions.auto_generate) formData.append('auto_generate', 'true');
            // A thread started from the Glean card (#435).
            if (replyOptions.entry) formData.append('entry', replyOptions.entry);

            response = await api.post("/nodes/", formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Set the node ID to trigger polling via useEffect
            const nodeId = response.data.id;
            setUploadedNodeId(nodeId);
            // Keep loading state active while transcription is in progress
            // Polling will start automatically via useEffect
            return;
          }
        } else {
          // Create a new text node
          response = await api.post("/nodes/", {
            content,
            parent_id: parentId,
            privacy_level: privacyLevel,
            ai_usage: aiUsage
          });
        }
        // Delete draft after successful save
        deleteDraft();
        setHasDraft(false);

        // Auto-generate path (without Agentic Reply): after creating
        // the plain /nodes/ entry, trigger an LLM response so the user
        // doesn't have to click LLM Response manually. Only fires when
        // the toggle is visible (top-level text entry with AI allowed).
        const shouldAutoGenerate = useAutoGenerate
          && allowAgenticPrompt
          && !editMode
          && !uploadedFile
          && !streamingSessionId
          && !parentId
          && aiUsage !== 'none'
          && !useAgenticPrompt  // agentic path already fires via /textmode/start
          && response?.data?.id;
        if (shouldAutoGenerate) {
          try {
            const llmRes = await api.post(
              `/nodes/${response.data.id}/llm`,
              { source_mode: 'textmode' },
            );
            succeed({
              ...response.data,
              id: llmRes.data.node_id,
              awaitLlm: llmRes.data.node_id,
            });
            setLoading(false);
            return;
          } catch (llmErr) {
            // LLM trigger failed — still fall through to plain success
            // so the user at least keeps their entry.
            console.error('Auto-generate LLM trigger failed:', llmErr);
          }
        }

        succeed(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error in NodeForm:", err);
        if (uploadedFile && isSpendCapError(err)) {
          // The client-side cap flag was stale; the server refused the
          // upload before storing anything (#341). The banner is up; the
          // toast says what happened to this press.
          addToast(spendCapToastMessage('upload'), 8000);
        } else {
          setError(err.response?.data?.error || err.message || "Error submitting form.");
        }
        setLoading(false);
        setIsUploading(false);
        setUploadProgress(0);
      }
    };

    useImperativeHandle(ref, () => ({
      submit: () => handleSubmit({ preventDefault: () => {} }),
      isDirty: () => content.trim().length > 0 || uploadedFile,
    }));

    // Explain why the record/upload buttons are disabled (#62). The most
    // common cause is "No AI Access" — audio needs transcription, which
    // requires AI access. Surfaced as a tooltip on both buttons.
    const audioDisabledReason =
      aiUsage === 'none'
        ? "Set AI Usage to Chat or Train to record or upload audio — transcription needs AI access."
        : !isOnline
        ? "You're offline — reconnect to record or upload audio."
        : isStreamingRecording
        ? "Finish the current recording first."
        : "";

    // Format time ago for last saved indicator
    const formatTimeAgo = (date) => {
      if (!date) return '';
      const seconds = Math.floor((new Date() - date) / 1000);
      if (seconds < 60) return 'just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      return `${hours}h ago`;
    };

    // Cmd+Return / Ctrl+Enter submits (#129). Mirrors the submit button's
    // enabled state: needs content (or an uploaded audio file) and must not
    // be loading / offline / mid-recording. Plain Enter still inserts a newline.
    const canSubmit =
      (content.trim().length > 0 || (!editMode && !!uploadedFile)) &&
      !loading && isOnline && !isStreamingRecording;
    useSubmitShortcut(textareaRef, () => handleSubmit(), canSubmit);

    return (
      <>
      <form onSubmit={handleSubmit}>
        {/* Text entry */}
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => {
            const newContent = e.target.value;
            setContent(newContent);
            // Auto-save draft when user types
            if (newContent.trim()) {
              saveDraft(newContent);
              setHasDraft(true);
            }
            // Discard uploaded file if user types
            if (!editMode && uploadedFile) {
              setUploadedFile(null);
            }
          }}
          onPaste={(e) => {
            // Per-entry cap: intercept pastes that would exceed it and
            // offer to split (new entries) or explain the limit
            // (edits / custom-submit conversations).
            const pasted = e.clipboardData
              ? e.clipboardData.getData("text") : "";
            if (!pasted) return;
            const el = e.target;
            const next = content.slice(0, el.selectionStart)
              + pasted + content.slice(el.selectionEnd);
            if (next.length <= NODE_CHAR_CAP) return;
            e.preventDefault();
            if (editMode || onSubmitOverride) {
              setError(
                `Pasting this would make the entry ` +
                `${next.length.toLocaleString()} characters — above the ` +
                `${NODE_CHAR_CAP.toLocaleString()}-character limit.`
              );
              return;
            }
            setPendingPaste(next);
            setShowSplitDialog(true);
          }}
          rows={compact ? 3 : 6}
          style={{
            width: "100%",
            backgroundColor: "var(--bg-input)",
            border: "1px solid var(--border)",
            borderRadius: "8px",
            padding: "14px 16px",
            fontFamily: "var(--sans)",
            fontSize: "1.05rem",
            fontWeight: 300,
            color: "var(--text-primary)",
            lineHeight: 1.6,
            height: compact ? "auto" : "clamp(120px, 40vh, 400px)",
            minHeight: compact ? "90px" : "120px",
            boxSizing: "border-box",
            transition: "border-color 0.3s ease",
            resize: "vertical",
          }}
          placeholder={placeholder || "What's present for you right now..."}
          disabled={!editMode && uploadedFile}
        />

        {/* Privacy Settings */}
        {!hidePowerFeatures && (
          <PrivacySelector
            privacyLevel={parentPrivacy === "public" && !editMode
              ? "public" : privacyLevel}
            aiUsage={aiUsage}
            onPrivacyChange={setPrivacyLevel}
            onAIUsageChange={setAiUsage}
            disabled={loading}
            lockPrivacy={parentPrivacy === "public" && !editMode}
          />
        )}

        {/* Agentic reply opt-in — only for new top-level text entries
            where AI is allowed. Mirrors the Craft-mode toggle visual
            from the NavBar. */}
        {allowAgenticPrompt && !editMode && !parentId && !hidePowerFeatures
            && aiUsage !== 'none' && (
          <div style={{ marginTop: '12px', marginBottom: '12px' }}>
            <button
              type="button"
              onClick={() => setUseAgenticPrompt(v => !v)}
              disabled={loading}
              style={{
                display: 'inline-flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '12px',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: loading ? 'not-allowed' : 'pointer',
                textAlign: 'left',
              }}
            >
              <label style={{
                display: 'block',
                fontFamily: 'var(--sans)',
                fontWeight: 400,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                color: 'var(--text-muted)',
                cursor: 'inherit',
              }}>
                Agentic Reply
              </label>
              <span style={{
                width: '32px',
                height: '18px',
                borderRadius: '9px',
                background: useAgenticPrompt ? 'var(--accent)' : 'var(--border)',
                position: 'relative',
                transition: 'background 0.2s ease',
                flexShrink: 0,
              }}>
                <span style={{
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  background: 'var(--text-primary)',
                  position: 'absolute',
                  top: '2px',
                  left: useAgenticPrompt ? '16px' : '2px',
                  transition: 'left 0.2s ease',
                }} />
              </span>
            </button>
            <div style={{
              fontSize: '0.8rem',
              color: 'var(--text-muted)',
              marginTop: '4px',
              fontFamily: 'var(--sans)',
              fontWeight: 300,
            }}>
              {useAgenticPrompt
                ? 'AI will include your profile, recent entries, todos and preferences as context.'
                : 'No agentic context. AI replies only see this entry.'}
            </div>
          </div>
        )}

        {/* Auto-generate — fire an LLM reply after the entry is saved,
            so the user doesn't have to click LLM Response manually.
            Shares the `loore_auto_generate` localStorage key with
            NodeDetail's inline toggle so there's one "auto-generate
            after submit" preference across the app. */}
        {allowAgenticPrompt && !editMode && !parentId && !hidePowerFeatures
            && aiUsage !== 'none' && (
          <div style={{ marginTop: '12px', marginBottom: '12px' }}>
            <button
              type="button"
              onClick={() => setUseAutoGenerate(v => !v)}
              disabled={loading}
              style={{
                display: 'inline-flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '12px',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: loading ? 'not-allowed' : 'pointer',
                textAlign: 'left',
              }}
            >
              <label style={{
                display: 'block',
                fontFamily: 'var(--sans)',
                fontWeight: 400,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                color: 'var(--text-muted)',
                cursor: 'inherit',
              }}>
                Auto-generate
              </label>
              <span style={{
                width: '32px',
                height: '18px',
                borderRadius: '9px',
                background: useAutoGenerate ? 'var(--accent)' : 'var(--border)',
                position: 'relative',
                transition: 'background 0.2s ease',
                flexShrink: 0,
              }}>
                <span style={{
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  background: 'var(--text-primary)',
                  position: 'absolute',
                  top: '2px',
                  left: useAutoGenerate ? '16px' : '2px',
                  transition: 'left 0.2s ease',
                }} />
              </span>
            </button>
            <div style={{
              fontSize: '0.8rem',
              color: 'var(--text-muted)',
              marginTop: '4px',
              fontFamily: 'var(--sans)',
              fontWeight: 300,
            }}>
              {useAutoGenerate
                ? 'AI will reply automatically after you submit.'
                : 'No AI reply. You can click LLM Response on the created node later.'}
            </div>
          </div>
        )}

        {isRecoveringAudio && (
          <div style={{
            padding: '8px 12px',
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            color: 'var(--text-secondary)',
            fontFamily: 'var(--sans)',
            fontSize: '0.85rem',
            fontWeight: 300,
            marginBottom: '8px',
          }}>
            Transcribing recovered audio...
          </div>
        )}

        {!isOnline && <div style={{ color: "var(--text-muted)", fontFamily: "var(--sans)", fontSize: "0.85rem", opacity: 0.8 }}>You're offline</div>}
        {error && <div style={{ color: "var(--accent)", fontFamily: "var(--sans)", fontSize: "0.9rem" }}>{error}</div>}

        {/* Auto-save status indicator */}
        {content.trim() && (
          <div style={{
            marginTop: '4px',
            fontSize: '0.85em',
            fontFamily: 'var(--sans)',
            fontWeight: 300,
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            {isDraftSaving ? (
              <>
                <span style={{ color: 'var(--accent)' }}>Saving...</span>
              </>
            ) : lastSaved ? (
              <>
                <span style={{ color: 'var(--text-muted)' }}>Draft saved {formatTimeAgo(lastSaved)}</span>
              </>
            ) : null}
          </div>
        )}

        {/* Display uploaded file info */}
        {uploadedFile && (
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'var(--text-secondary)',
            fontFamily: 'var(--sans)',
            fontSize: '0.85rem',
          }}>
            <span>{uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
            <button
              type="button"
              onClick={() => {
                setUploadedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              style={{ marginLeft: '8px', padding: '4px 8px', cursor: 'pointer' }}
            >
              Remove
            </button>
          </div>
        )}

        {!hideSubmit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
            <button
              type="submit"
              disabled={loading || !isOnline || isStreamingRecording}
              style={{
                borderColor: 'var(--accent)',
                color: 'var(--accent)',
                opacity: !isOnline || isStreamingRecording ? 0.35 : 1,
                cursor: loading || !isOnline || isStreamingRecording ? 'not-allowed' : 'pointer',
              }}
            >
              {isUploading
                ? `Uploading... ${uploadProgress}%`
                : loading && transcriptionStatus === 'processing' && transcriptionProgress > 0
                ? `Transcribing... ${transcriptionProgress}%`
                : loading && transcriptionStatus === 'pending'
                ? "Waiting to transcribe..."
                : loading
                ? "Sending..."
                : "Send"}
            </button>
            {hasDraft && (
              <button
                type="button"
                onClick={handleDiscardDraft}
                disabled={loading}
                style={{ padding: '8px 16px', cursor: 'pointer' }}
              >
                Discard draft
              </button>
            )}
            {!editMode && (
              <>
                <StreamingMicButton
                  parentId={parentId}
                  privacyLevel={privacyLevel}
                  aiUsage={aiUsage}
                  disabledReason={audioDisabledReason}
                  onRecordingStart={() => {
                    // Capture content before streaming starts so we can append to it
                    preStreamingContentRef.current = content;
                    setIsStreamingRecording(true);
                  }}
                  onTranscriptUpdate={(transcript) => {
                    // Append new transcript to pre-existing content
                    const prefix = preStreamingContentRef.current;
                    const separator = prefix && transcript ? '\n\n' : '';
                    setContent(prefix + separator + transcript);
                  }}
                  onComplete={(data) => {
                    // Don't navigate immediately - let user review/edit the transcript first
                    // With draft-based streaming, no node exists yet - just a draft
                    setLoading(false);
                    setIsStreamingRecording(false);
                    // Append final transcript to pre-existing content
                    const prefix = preStreamingContentRef.current;
                    const separator = prefix && data.content ? '\n\n' : '';
                    const combinedContent = prefix + separator + data.content;
                    setContent(combinedContent);
                    setStreamingSessionId(data.sessionId);
                    // Save combined content to the regular draft so it persists on reopen
                    saveDraft(combinedContent);
                    setHasDraft(true);
                  }}
                  onError={(err) => {
                    // A start refused by the monthly spend cap is not a
                    // failure; the recorder already showed a toast (#341).
                    if (!err?.spendCapped) {
                      setError(err.message || 'Streaming transcription failed');
                    }
                    setLoading(false);
                    setIsStreamingRecording(false);
                  }}
                  disabled={loading || uploadedFile || aiUsage === 'none'}
                />
                {!hideAudioUpload && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".mp3,.wav,.m4a,.webm,.ogg,.oga,.flac,.aac,.mp4,.mpeg,.mpga,audio/*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    {/* Tooltip on the wrapper span, not the disabled button —
                        disabled elements don't fire hover, so a title on the
                        button itself never shows (#62). */}
                    <span
                      title={(isStreamingRecording || aiUsage === 'none' || !isOnline) ? (audioDisabledReason || undefined) : undefined}
                      style={{ display: 'inline-flex', cursor: isStreamingRecording || aiUsage === 'none' || !isOnline ? 'not-allowed' : 'default' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          // Refuse before the file picker opens: a capped
                          // user would otherwise pick and send a file only
                          // to have it refused (#341).
                          if (isSpendBlocked()) {
                            notifySpendBlocked();
                            addToast(spendCapToastMessage('upload'), 8000);
                            return;
                          }
                          fileInputRef.current?.click();
                        }}
                        disabled={isStreamingRecording || aiUsage === 'none' || !isOnline}
                        style={{ padding: '8px 16px', cursor: isStreamingRecording || aiUsage === 'none' || !isOnline ? 'not-allowed' : 'pointer', opacity: isStreamingRecording || aiUsage === 'none' || !isOnline ? 0.35 : 1, pointerEvents: isStreamingRecording || aiUsage === 'none' || !isOnline ? 'none' : 'auto' }}
                      >
                        Upload
                      </button>
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </form>
      <RegenerateTtsDialog
        open={showTtsDialog}
        onClose={() => setShowTtsDialog(false)}
        onChoice={(regenerate) => {
          setShowTtsDialog(false);
          // Resume the save with the user's choice (no event object).
          handleSubmit(null, regenerate);
        }}
      />
      <ApplyToRepliesDialog
        open={showScopeDialog}
        privacyChanged={initialPrivacyLevel != null && privacyLevel !== initialPrivacyLevel}
        aiUsageChanged={initialAiUsage != null && aiUsage !== initialAiUsage}
        onClose={() => setShowScopeDialog(false)}
        onChoice={(applyToReplies) => {
          setShowScopeDialog(false);
          // Resume the save with the scope chosen, keeping any TTS choice
          // already made (no event object).
          handleSubmit(
            null, pendingResumeRef.current.regenerateTts,
            undefined, undefined, applyToReplies,
          );
        }}
      />
      <PublicReplyDialog
        open={showPublicReplyDialog}
        onClose={() => setShowPublicReplyDialog(false)}
        onConfirm={() => {
          setShowPublicReplyDialog(false);
          // Resume the send with public consent given (no event object).
          handleSubmit(null, undefined, undefined, true);
        }}
      />
      <SplitContentDialog
        open={showSplitDialog}
        charCount={pendingPaste ? pendingPaste.length : content.length}
        onClose={() => {
          setShowSplitDialog(false);
          setPendingPaste(null);
        }}
        onConfirm={() => {
          setShowSplitDialog(false);
          setSplitAcknowledged(true);
          if (pendingPaste) {
            // Paste path: insert the text; the split happens at save.
            setContent(pendingPaste);
            saveDraft(pendingPaste);
            setHasDraft(true);
            setPendingPaste(null);
          } else {
            // Submit path: resume the save (no event object); the
            // server splits into connected entries.
            handleSubmit(null, undefined, true);
          }
        }}
      />
      </>
    );
  }
);

export default NodeForm;
